import { useEffect, useMemo, useRef, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { cn } from "../../../lib/utils";

/**
 * 15-minute aligned time-of-day picker backed by a Radix Popover listbox.
 *
 * Modes:
 *  - kind="start": 96 options (00:00 .. 23:45).
 *  - kind="end":   97 options (00:00 .. 23:45 + "24:00" sentinel rendered as
 *                  "12:00 AM (end of day)"). When `startTime` is provided,
 *                  options earlier-or-equal-to startTime are filtered out (the
 *                  "24:00" sentinel is always kept).
 *
 * Emits the underlying "HH:MM" (or "24:00") string via onChange.
 */

const ALL_TIMES = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
});

function fmt12(hhmm) {
  if (hhmm === "24:00") return "12:00 AM (EOD)";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function toMin(s) {
  if (s === "24:00") return 24 * 60;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
}

function durationLabel(startHHMM, endHHMM) {
  const diff = toMin(endHHMM) - toMin(startHHMM);
  if (diff <= 0) return "— invalid";
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

export function TimePicker({
  value,
  onChange,
  kind,
  startTime,
  ariaLabel,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const listboxRef = useRef(null);
  const selectedRef = useRef(null);

  const options = useMemo(() => {
    if (kind !== "end") return ALL_TIMES;
    // Filter end options to those strictly after startTime; always keep the
    // "24:00" (end-of-day) sentinel so single-day "11:45 PM – midnight" works.
    const startMin = startTime != null ? toMin(startTime) : -Infinity;
    return [...ALL_TIMES.filter((t) => toMin(t) > startMin), "24:00"];
  }, [kind, startTime]);

  // Auto-scroll so the selected option sits centered in the listbox when the
  // popover opens. We compute scrollTop manually rather than calling
  // scrollIntoView because Radix portals the content + positions it via
  // transforms; scrollIntoView can fire before layout is stable. RAF gives
  // the browser a frame to commit layout before we measure.
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => {
      const listbox = listboxRef.current;
      const selected = selectedRef.current;
      if (!listbox || !selected) return;
      const target =
        selected.offsetTop - listbox.clientHeight / 2 + selected.clientHeight / 2;
      listbox.scrollTop = Math.max(0, target);
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const triggerLabel = fmt12(value);

  const handleSelect = (optionValue) => {
    onChange?.(optionValue);
    setOpen(false);
  };

  const handleListKeyDown = (event) => {
    const container = listboxRef.current;
    if (!container) return;
    const items = Array.from(container.querySelectorAll('[role="option"]'));
    if (items.length === 0) return;
    const active = document.activeElement;
    const idx = items.indexOf(active);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = items[Math.min(items.length - 1, idx < 0 ? 0 : idx + 1)];
      next?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prev = items[Math.max(0, idx < 0 ? 0 : idx - 1)];
      prev?.focus();
    } else if (event.key === "Enter") {
      if (idx >= 0) {
        event.preventDefault();
        const v = items[idx].getAttribute("data-value");
        if (v) handleSelect(v);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "inline-flex h-9 min-w-[7.5rem] items-center justify-between rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
          )}
        >
          <span>{triggerLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-1"
        onOpenAutoFocus={(e) => {
          // Let our auto-scroll effect handle focus framing; prevent Radix
          // from focusing the first child which would yank scroll position.
          e.preventDefault();
        }}
      >
        <div
          ref={listboxRef}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
          onKeyDown={handleListKeyDown}
          onWheel={(e) => e.stopPropagation()}
          className="max-h-72 overflow-y-auto overscroll-contain py-1"
        >
          {options.map((opt) => {
            const isSelected = opt === value;
            let label = fmt12(opt);
            if (kind === "end" && startTime != null) {
              label += ` (${durationLabel(startTime, opt)})`;
            }

            return (
              <div
                key={opt}
                role="option"
                aria-selected={isSelected}
                data-value={opt}
                tabIndex={0}
                ref={isSelected ? selectedRef : undefined}
                onClick={() => handleSelect(opt)}
                className={cn(
                  "cursor-pointer select-none rounded-lg px-3 py-1.5 text-sm",
                  isSelected
                    ? "bg-brand-primary/10 text-brand-primary dark:bg-brand-primary/25 dark:text-blue-100"
                    : "text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800"
                )}
              >
                {label}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
